import React, { useRef, useState } from 'react';
import SEO from '@/components/seo/SEO';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Download, Megaphone, Loader2, ClipboardCheck, ArrowRight } from 'lucide-react';
import { Breadcrumbs } from '@/components/seo/Breadcrumbs';
import { generateToolboxTalkPDF } from '@/lib/pdfGenerator';
import { toolboxTalks, ToolboxTalkContent } from '@/lib/content/resources/toolboxTalks';
import { toast } from 'sonner';
import { getSignupUrl } from '@/lib/appLinks';
import { logFirebaseEvent, trackSignupIntent } from '@/lib/tracking';

const ToolboxTalksPage: React.FC = () => {
  const [generatingId, setGeneratingId] = useState<string | null>(null);
  const busy = useRef(false);
  const [downloadStatus, setDownloadStatus] = useState<'idle' | 'ready' | 'failed'>('idle');
  const sampleLink = (source: string) => (
    <Button asChild><a href="/demo/sample-inspection" onClick={() => void logFirebaseEvent('sample_inspection_intent', { source })}>Try a sample inspection <ArrowRight className="ml-2 h-4 w-4" /></a></Button>
  );

  const jsonLd = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
          {
              "@type": "Question",
              "name": "Who needs stormwater training under the EPA Construction General Permit?",
              "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Part 6 covers stormwater-team personnel responsible for controls, inspections, and corrective actions. Inspectors have additional qualification requirements under Part 6.3."
              }
          },
          {
              "@type": "Question",
              "name": "Does a toolbox talk make someone a qualified EPA construction inspector?",
              "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "No. A short toolbox talk can reinforce job-specific practices, but it does not replace the inspector training or current certification described in Part 6.3 of the EPA permit."
              }
          }
      ]
  };

  const handleDownload = async (talk: ToolboxTalkContent) => {
      if (busy.current) return;
      busy.current = true;
      setDownloadStatus('idle');
      void logFirebaseEvent('toolbox_download_attempt', { talk_id: talk.id });

      setGeneratingId(talk.id);
      const toastId = toast.loading("Preparing meeting script...");
      
      try {
          await generateToolboxTalkPDF(talk);
          setDownloadStatus('ready');
          void logFirebaseEvent('toolbox_pdf_generated', { talk_id: talk.id });
          toast.success("Download started!", { id: toastId });
      } catch (e) {
          console.error(e);
          setDownloadStatus('failed');
          void logFirebaseEvent('toolbox_download_failed', { talk_id: talk.id });
          toast.error("Failed to generate PDF. Please try again.", { id: toastId });
      } finally {
          setGeneratingId(null);
          busy.current = false;
      }
  };

  return (
    <div className="min-h-screen bg-background font-sans selection:bg-orange-500/20">
      <SEO 
        title="Free SWPPP Toolbox Talks - Safety Meeting PDFs"
        description="Download free 5-minute safety meeting scripts (Toolbox Talks) for educating construction crews on stormwater compliance. Includes Stop Work Authority and Spill Response."
        canonical="/resources/toolbox-talks"
        jsonLd={jsonLd}
      />

      <div className="bg-muted/30 py-20 border-b border-border">
        <div className="container px-4 text-center max-w-3xl mx-auto">
           <Breadcrumbs items={[
               { label: 'Resources', href: '/' },
               { label: 'Toolbox Talks', href: '/resources/toolbox-talks' }
           ]} />

           <div className="inline-flex items-center justify-center p-3 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 rounded-full mb-6">
             <Megaphone size={32} />
           </div>
           <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
             SWPPP Toolbox Talks
           </h1>
           <p className="text-xl text-muted-foreground">
             Free, focused meeting scripts for reinforcing erosion control, spill response, housekeeping, and inspection practices with field crews.
           </p>
           <p className="mt-4 text-muted-foreground">Download the PDFs below without an account. Then try recording the crew’s site observations in a sample digital inspection.</p>
           <div className="mt-6">{sampleLink('toolbox_intro')}</div>
        </div>
      </div>

      <div className="container px-4 py-16 max-w-5xl mx-auto">
        {downloadStatus === 'failed' && <p role="alert" className="mb-6 rounded-lg border border-destructive p-4">Failed to generate PDF. Please try again.</p>}
        {downloadStatus === 'ready' && <div role="status" className="mb-8 rounded-xl border p-6">
          <h2 className="text-xl font-bold">Your meeting PDF is ready.</h2>
          <p className="my-3">Look in your browser downloads. Ready to turn observations into an inspection report? Try the sample—no account required.</p>
          {sampleLink('toolbox_after_download')}
        </div>}
        <div className="grid md:grid-cols-2 gap-6 mb-24">
            {toolboxTalks.map((t) => (
                <Card key={t.id} className={`flex flex-col border-border transition-colors group ${t.highlight ? 'border-orange-500/40 bg-orange-50/10 shadow-lg' : 'hover:border-orange-500/50'}`}>
                    <CardHeader>
                        <div className="flex justify-between items-start">
                            <div className="flex gap-2 mb-2">
                                {t.tags.map(tag => (
                                    <span key={tag} className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground bg-muted px-2 py-1 rounded">
                                        {tag}
                                    </span>
                                ))}
                            </div>
                            <t.icon size={20} className="text-orange-600" />
                        </div>
                        <CardTitle className="text-xl group-hover:text-orange-600 transition-colors">{t.title}</CardTitle>
                        <CardDescription className="text-base">{t.description}</CardDescription>
                    </CardHeader>
                    <CardFooter className="mt-auto pt-0">
                        <Button 
                            variant="outline" 
                            className="w-full gap-2 group-hover:bg-orange-50 dark:group-hover:bg-orange-900/10 group-hover:text-orange-700 group-hover:border-orange-200" 
                            onClick={() => handleDownload(t)}
                            disabled={generatingId !== null}
                        >
                            {generatingId === t.id ? (
                                <Loader2 size={16} className="animate-spin" />
                            ) : (
                                <Download size={16} />
                            )}
                            {generatingId === t.id ? "Generating PDF..." : "Download PDF Script"}
                        </Button>
                    </CardFooter>
                </Card>
            ))}
        </div>

        <div className="mb-20 flex flex-col items-start justify-between gap-6 rounded-2xl border border-primary/20 bg-primary/5 p-6 sm:flex-row sm:items-center md:p-8">
            <div className="flex items-start gap-4">
                <div className="rounded-xl bg-primary p-3 text-primary-foreground"><ClipboardCheck className="h-6 w-6" /></div>
                <div>
                    <h2 className="text-xl font-bold">Turn the talk into a field record</h2>
                    <p className="mt-1 max-w-xl text-sm text-muted-foreground">Create your first project and inspection free—no credit card required.</p>
                </div>
            </div>
            <Button className="whitespace-nowrap" asChild>
                <a href={getSignupUrl()} onClick={() => trackSignupIntent('toolbox_talks_after_downloads')}>Start Inspecting <ArrowRight className="ml-2 h-4 w-4" /></a>
            </Button>
        </div>
        
        {/* SEO CONTENT SECTION */}
        <div className="prose dark:prose-invert max-w-3xl mx-auto pt-8 border-t border-border">
            <h2>The "Training Log" Requirement</h2>
            <p>
                The EPA Construction General Permit (CGP) requires relevant stormwater-team personnel to understand the permit requirements related to their responsibilities. It does not impose the same training requirement on every person who enters a site.
            </p>
            <p>
                Specifically, <strong>Part 6 of the 2022 EPA CGP</strong> mandates training for:
            </p>
            <ul>
                <li>Personnel responsible for the design, installation, maintenance, and/or repair of stormwater controls.</li>
                <li>Personnel responsible for conducting inspections.</li>
                <li>Personnel responsible for taking corrective actions.</li>
            </ul>
            <p>
                Inspectors have separate qualification requirements under Part 6.3. A toolbox talk can reinforce field practices, but it does not by itself make an attendee a qualified inspector. Review the <a href="https://www.epa.gov/npdes/construction-general-permit-inspector-training" target="_blank" rel="noopener noreferrer">EPA inspector training requirements</a> and your own permit before assigning inspection duties.
            </p>
            <h3>Why Toolbox Talks?</h3>
            <p>
                Short, focused meetings help translate a project SWPPP into clear tasks: where controls are located, what damage to report, how to handle materials, and who owns corrective action.
            </p>
            <p>
                Document the topic, date, presenter, attendees, site-specific examples, and follow-up actions. The PDF downloads include a sign-in sheet for that recordkeeping. Keep the record with the SWPPP when your permit requires it.
            </p>

            <div className="not-prose mt-8 p-6 bg-orange-50 dark:bg-orange-900/10 rounded-xl border border-orange-100 dark:border-orange-900">
                <h4 className="font-bold text-lg mb-2 text-orange-900 dark:text-orange-100">Primary source</h4>
                <p className="text-orange-800 dark:text-orange-200 mb-4 text-sm">
                    Requirements vary by permitting authority and project. Start with Part 6 of the applicable permit; for EPA-administered projects, use the current EPA CGP and inspector-training page.
                </p>
                <a className="text-sm font-bold text-orange-900 underline dark:text-orange-100" href="https://www.epa.gov/npdes/2022-construction-general-permit-cgp" target="_blank" rel="noopener noreferrer">Read the current EPA Construction General Permit</a>
            </div>
            <p className="text-sm text-muted-foreground">Official sources last reviewed September 12, 2026.</p>
        </div>
      </div>
    </div>
  );
};

export default ToolboxTalksPage;
